/**
 * Client-side mirror of `backend/services/deal_scoring.py`.
 *
 * IMPORTANT: keep this file in lockstep with the Python engine.
 * All persistent scoring (exports, emails, saved alerts, bulk analyze
 * jobs) MUST run through the Python engine — this JS copy exists only
 * so the dashboard can re-score visible properties instantly when the
 * user changes their strategy without re-hitting the API.
 */

const norm_cash_flow = (cf) => {
  if (cf <= -500) return 0;
  if (cf >= 1000) return 1;
  if (cf < 0) return (cf + 500) / 500 * 0.30;
  if (cf < 500) return 0.30 + (cf / 500) * 0.45;
  return 0.75 + ((cf - 500) / 500) * 0.25;
};

const norm_cash_on_cash = (coc) => coc <= 0 ? 0 : Math.min(coc / 12, 1);

const norm_cap_rate = (cap) => cap <= 0 ? 0 : Math.min(cap / 10, 1);

const norm_appreciation = (purchase_price, zestimate) => {
  if (!zestimate || zestimate <= 0 || purchase_price <= 0) return 0.4;
  const delta_pct = (zestimate - purchase_price) / purchase_price;
  if (delta_pct <= 0) return 0;
  return Math.min(delta_pct / 0.20, 1);
};

const norm_rent_to_price = (rent, price) => {
  if (price <= 0) return 0;
  const ratio_pct = (rent / price) * 100;
  if (ratio_pct <= 0.4) return 0;
  if (ratio_pct >= 1.2) return 1;
  if (ratio_pct < 0.7) return (ratio_pct - 0.4) / 0.3 * 0.35;
  if (ratio_pct < 1.0) return 0.35 + (ratio_pct - 0.7) / 0.3 * 0.45;
  return 0.80 + (ratio_pct - 1.0) / 0.2 * 0.20;
};

const norm_risk = (expense_ratio_pct, vacancy_rate_pct) => {
  if (expense_ratio_pct <= 0) return 0.5;
  const er_score = Math.max(0, Math.min(1, (60 - expense_ratio_pct) / 30));
  const vac_score = Math.max(0, Math.min(1, (12 - vacancy_rate_pct) / 8));
  return er_score * 0.75 + vac_score * 0.25;
};

export const DEFAULT_WEIGHTS = {
  cash_flow: 25, cash_on_cash: 25, cap_rate: 20, appreciation: 15, rent_to_price: 10, risk: 5,
};

/**
 * Score an analysis payload against strategy weights.
 * Returns a number 0-100.
 */
export function scoreAnalysis(analysis, weights = DEFAULT_WEIGHTS) {
  if (!analysis) return 0;
  const prop = analysis.property || {};
  const zip = analysis.zip_data || {};
  const vacancy_pct = Number(zip.vacancy_rate ?? 6);
  const purchase_price = Number(analysis.purchase_price || 0);
  const cash_flow = Number(analysis.cash_flow_monthly || 0);
  const coc = Number(analysis.cash_on_cash_return || 0);
  const cap = Number(analysis.cap_rate || 0);
  const rent = Number(analysis.estimated_monthly_rent || 0);
  const zestimate = Number(prop.zestimate || 0) || null;
  let expense_ratio = Number(analysis.expense_ratio || 0);
  if (!expense_ratio) {
    const egi = Number(analysis.effective_gross_income || 0);
    const exp = Number(analysis.total_monthly_expenses || 0);
    expense_ratio = egi > 0 ? (exp / egi) * 100 : 0;
  }
  const normalized = {
    cash_flow: norm_cash_flow(cash_flow),
    cash_on_cash: norm_cash_on_cash(coc),
    cap_rate: norm_cap_rate(cap),
    appreciation: norm_appreciation(purchase_price, zestimate),
    rent_to_price: norm_rent_to_price(rent, purchase_price),
    risk: norm_risk(expense_ratio, vacancy_pct),
  };
  let total = 0;
  for (const key of Object.keys(DEFAULT_WEIGHTS)) {
    total += normalized[key] * Number(weights[key] || 0);
  }
  return Math.round(Math.max(0, Math.min(100, total)));
}
